import type { QuizOptionSpec } from "@-/dev.improve.haniverse.assets.slides";

/** FNV-1a: a stable 32-bit seed from the showing's identity. */
function hashSeed(seed: string): number {
  let hash = 0x81_1c_9d_c5;
  for (let index = 0; index < seed.length; index++) {
    hash ^= seed.codePointAt(index) ?? 0;
    hash = Math.imul(hash, 0x01_00_01_93);
  }
  return hash >>> 0;
}

/** mulberry32: a tiny seedable PRNG, so one showing always draws the same order. */
function mulberry32(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state + 0x6d_2b_79_f5) >>> 0;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  };
}

/**
 * The quiz's options in the order one showing presents them. Seeded by the showing (its
 * presentation key), so the order holds while the card is on screen but differs between showings:
 * a learner answering a due quiz again cannot lean on where the answer sat last time.
 */
export function shuffleQuizOptions<TOption extends QuizOptionSpec>(
  options: readonly TOption[],
  seed: string,
): TOption[] {
  const random = mulberry32(hashSeed(seed));
  const shuffled = [...options];
  for (let index = shuffled.length - 1; index > 0; index--) {
    const swapIndex = Math.floor(random() * (index + 1));
    const option = shuffled[index];
    const swapOption = shuffled[swapIndex];
    if (option === undefined || swapOption === undefined) {
      continue;
    }
    shuffled[index] = swapOption;
    shuffled[swapIndex] = option;
  }
  return shuffled;
}
